/**
 * ATL-1589 — «Предстоящие расходы за текущий период»: what the business still has to pay out for
 * the period's accrued expenses (salaries, supplies, recurring spendings).
 *
 * WHY THIS EXISTS
 *
 * The Cash Flow report computed it as one netted difference per CATEGORY:
 *
 *   (salaries.totalDebit + suppliers.totalDebit + recurring.totalDebit)
 *     − (salaries.totalCreditCurrentPeriod + suppliers.totalCreditCurrentPeriod + recurring.totalCreditCurrentPeriod)
 *
 * so a payee paid MORE than its accrual contributed a negative "upcoming expense" that silently
 * cancelled what other payees are still owed. Measured on UNDERWOOD DECOR, September 2026: the
 * recurring spending «ТОО СтройАстэс Аренда БАЗА» is configured at 270 000 a month but is actually
 * paid 670 000 (July, August and September alike), leaving −400 000; salaries still owed for the
 * period were 525 183; the report printed 525 183 − 400 000 = 125 183 as the money still to pay.
 * The merchant still had to pay all 525 183 of those salaries — an overpaid landlord does not pay
 * an employee.
 *
 * So each payee — one employee, one supplier, one recurring spending — is settled on its own, and
 * one that is fully paid or overpaid contributes zero, never a negative amount. The overpayment is
 * not lost: it is already inside «Фактические расходы за текущий период» (the money really left),
 * so «Ожидаемый баланс» now reads as cash after every remaining obligation is paid, which is what
 * its description says.
 *
 * The per-payee amount is the same `totalDebit − totalCreditCurrentPeriod` the payee's own
 * «Баланс за текущий период» line and its «оплатить» prefill (`prefilledBalance`) are built from.
 * That line prints `credit − debit`, so the headline is the sum of the NEGATIVE per-payee balance
 * lines below it, sign flipped; a zero or positive line contributes nothing.
 *
 * Money arithmetic goes through `addDecimalAmounts`: both sides are float accumulations (supply
 * totals and pro-rated payments are fractional), and a payee settled to the cent can subtract to
 * `4.44e-16`, which must count as settled rather than as an upcoming expense (ATL-1371).
 */
import { addDecimalAmounts } from '../../utils/decimalInput'

/** One payee's period figures, as `PaymentStatsScreen` accumulates them. */
export interface PayeePeriodBalance {
  totalDebit?: number
  totalCreditCurrentPeriod?: number
}

/**
 * A category as the screen builds it: its own aggregate NUMBERS (`totalDebit`, `totalCredit`,
 * `allTimeDebit`, `totalUnpaidSumPreviousPeriod`, …) side by side with one OBJECT per payee.
 */
export type PaymentCategory = Record<string, unknown> | null | undefined

const isPayee = (value: unknown): value is PayeePeriodBalance =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

/**
 * What is still to be paid to one payee for the current period: its accrual minus what was paid
 * for this period, and never less than zero.
 *
 * An absent side is a genuine zero, not a masked error: a payee that only received payments has no
 * `totalDebit`, and one nobody has paid yet has no `totalCreditCurrentPeriod`. A side that IS
 * present but is not a finite number yields `NaN`, so the report shows an unreadable figure as
 * unreadable instead of dropping that payee from the total.
 */
export const outstandingForPayee = (payee: PayeePeriodBalance): number => {
  // `in`, not `??`: a key that is PRESENT with `undefined`/`null` (a recurring spending whose
  // `amount` is missing arrives as `{ totalDebit: undefined }`) is an unreadable amount, not zero.
  const debit = 'totalDebit' in payee ? payee.totalDebit : 0
  const credit = 'totalCreditCurrentPeriod' in payee ? payee.totalCreditCurrentPeriod : 0
  // Checked before negating: `-'5'` is the number -5, which would slip past the helper's guard.
  if (typeof debit !== 'number' || typeof credit !== 'number') return NaN
  const balance = addDecimalAmounts(debit, -credit)
  if (balance === null) return NaN
  return balance > 0 ? balance : 0
}

/**
 * «Предстоящие расходы за текущий период» — the sum over every payee of every category of what is
 * still owed to it (`outstandingForPayee`).
 *
 * A category that has not loaded yet (`null`/`undefined`) makes the figure `NaN` rather than
 * quietly leaving that category's debts out.
 */
export const calculateUpcomingPayments = (...categories: PaymentCategory[]): number => {
  let total = 0
  for (const category of categories) {
    if (!category) return NaN
    for (const value of Object.values(category)) {
      if (!isPayee(value)) continue
      const next = addDecimalAmounts(total, outstandingForPayee(value))
      if (next === null) return NaN
      total = next
    }
  }
  return total
}
